import React, { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import {
    MoreVertical, ExternalLink, Download, Pin, Trash2, Eye, X, Hash, Plus,
    FileText, Check, MessageSquarePlus, GripVertical, Sparkles,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import Tip from '../ui/tip';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem,
    DropdownMenuTrigger, DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import { cn } from '../../lib/utils';
import { getFileIcon, getMatchSnippet, visibleTags, fileSource, shortAgo } from './fileMeta';
import { AdvisorBadge } from './FileCard';

/**
 * An artifact as a row, at two densities.
 *
 * `compact` is the shelf's density and it is not just "the same row, smaller":
 * a shelf column can be 240px wide, so every fixed-width element in the roomy
 * layout has to go or share a slot. The type icon and the checkbox occupy one
 * 20px square between them, the action menu floats over the row's tail instead
 * of reserving space, and the timestamp collapses to "3d". What the two share is
 * the action menu itself and the rename affordance, so what you can *do* with an
 * artifact can't drift between views.
 */
function FileRow({
    file,
    isSelected,
    isChecked,
    selectionActive,
    query,
    compact,
    dragHandle,
    onOpen,
    onPreview,
    onPin,
    onDownload,
    onDelete,
    onRename,
    onUpdateTags,
    onToggleSelect,
    onSendToAdvisor,
    readOnly,
}) {
    const [isRenaming, setIsRenaming] = useState(false);
    const [nameValue, setNameValue] = useState(file.name);
    const [isAddingTag, setIsAddingTag] = useState(false);
    const [newTag, setNewTag] = useState('');

    const tags = visibleTags(file);
    const isAdvisor = fileSource(file) === 'advisor';
    const snippet = query ? getMatchSnippet(file, query) : null;

    const handleSaveRename = () => {
        if (nameValue.trim() && nameValue !== file.name) onRename(file, nameValue);
        setIsRenaming(false);
    };

    const handleAddTag = () => {
        const t = newTag.trim();
        if (t && !(file.tags || []).includes(t)) onUpdateTags(file, [...(file.tags || []), t]);
        setNewTag('');
        setIsAddingTag(false);
    };

    const menuProps = {
        file, onOpen, onPreview, onDownload, onSendToAdvisor, onPin, onDelete, readOnly,
        onStartRename: () => setIsRenaming(true),
    };

    const renameField = (
        <div className="flex items-center gap-2">
            <Input
                value={nameValue}
                onChange={(e) => setNameValue(e.target.value)}
                onBlur={handleSaveRename}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveRename();
                    if (e.key === 'Escape') { setNameValue(file.name); setIsRenaming(false); }
                }}
                className={cn('h-7 text-sm', compact && 'h-6 text-xs')}
                autoFocus
            />
            <button onClick={handleSaveRename} className="p-1 hover:text-green-500" aria-label="Save name">
                <Check className="h-4 w-4" />
            </button>
            <button onClick={() => setIsRenaming(false)} className="p-1 hover:text-red-500" aria-label="Cancel rename">
                <X className="h-4 w-4" />
            </button>
        </div>
    );

    // ─── Shelf density ───────────────────────────────────────────────────────
    if (compact) {
        return (
            <div
                data-testid="file-row"
                className={cn(
                    'group relative flex items-center gap-1.5 rounded-xl border border-transparent py-1 pl-0.5 pr-1 transition-colors',
                    'hover:border-white/10 hover:bg-white/[0.04]',
                    file.pinned && 'bg-accent/[0.06]',
                    isSelected && 'border-primary/50 bg-primary/10',
                    isChecked && 'border-primary bg-primary/15',
                )}
            >
                {/* Reserves its 12px whether or not it's showing, so revealing it
                    on hover doesn't shove the filename sideways. */}
                {dragHandle}

                {/* Type icon and checkbox share one square. Side by side they cost
                    ~40px of a 240px column, which is what left the filenames in
                    the shelf truncated to a single character. */}
                <span className="relative flex h-5 w-5 flex-shrink-0 items-center justify-center">
                    <span
                        className={cn(
                            'transition-opacity',
                            !readOnly && 'group-hover:opacity-0',
                            !readOnly && (isChecked || selectionActive) && 'opacity-0'
                        )}
                    >
                        {getFileIcon(file.type, file.name, 'h-4 w-4')}
                    </span>
                    {!readOnly && (
                        <Tip label={isChecked ? `Deselect ${file.name}` : `Select ${file.name}`} side="top">
                            <button
                                onClick={(e) => { e.stopPropagation(); onToggleSelect(file, e.shiftKey); }}
                                aria-pressed={isChecked}
                                data-testid="file-row-select"
                                className={cn(
                                    'absolute inset-0 flex items-center justify-center rounded-md border opacity-0 transition-all group-hover:opacity-100 max-md:opacity-100',
                                    isChecked
                                        ? 'border-primary bg-primary text-primary-foreground opacity-100'
                                        : 'border-white/25 bg-secondary text-transparent hover:border-primary/60',
                                    selectionActive && 'opacity-100'
                                )}
                            >
                                <Check className="h-3 w-3" />
                            </button>
                        </Tip>
                    )}
                </span>

                <div className="min-w-0 flex-1">
                    {isRenaming ? renameField : (
                        <>
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => onPreview(file)}
                                    title={file.name}
                                    className="min-w-0 truncate text-left font-mono text-[11px] font-medium leading-tight transition-colors hover:text-primary"
                                >
                                    {file.name}
                                </button>
                                {file.pinned && <Pin className="h-2.5 w-2.5 flex-shrink-0 fill-accent text-accent" />}
                                {/* The full Advisor badge is ~64px — a fifth of the
                                    column. The mark alone carries it here. */}
                                {isAdvisor && (
                                    <Sparkles className="h-2.5 w-2.5 flex-shrink-0 text-accent" aria-label="Written by the Advisor" />
                                )}
                            </div>

                            {snippet ? (
                                <p className="truncate font-mono text-[9px] leading-tight text-muted-foreground">
                                    {snippet.before}
                                    <mark className="rounded-sm bg-primary/25 px-0.5 text-primary">{snippet.match}</mark>
                                    {snippet.after}
                                </p>
                            ) : (
                                /* No category here — the column it's sitting in is
                                   the category. */
                                <p className="truncate text-[9px] leading-tight text-muted-foreground/60">
                                    {shortAgo(file.last_edited)}
                                    {tags.length > 0 && ` · #${tags[0]}`}
                                    {tags.length > 1 && ` +${tags.length - 1}`}
                                </p>
                            )}
                        </>
                    )}
                </div>

                {/* Floats over the row's tail rather than taking a column of its
                    own — it's opaque, so the filename simply runs under it. */}
                {!isRenaming && (
                    <div className="absolute right-1 top-1/2 -translate-y-1/2 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100 max-md:opacity-100">
                        <RowMenu
                            {...menuProps}
                            triggerClassName="h-6 w-6 rounded-md bg-secondary/95 shadow-lg shadow-black/40 backdrop-blur-sm [&_svg]:h-3.5 [&_svg]:w-3.5"
                        />
                    </div>
                )}
            </div>
        );
    }

    // ─── List density ────────────────────────────────────────────────────────
    return (
        <div
            data-testid="file-row"
            className={cn(
                'group flex items-center gap-3 rounded-2xl border border-white/10 bg-secondary/20 p-4 transition-all hover:border-primary/30',
                file.pinned && 'ring-2 ring-accent/30',
                isSelected && 'border-primary/50 ring-2 ring-primary/40',
                isChecked && 'border-primary ring-2 ring-primary/60',
            )}
        >
            {!readOnly && (
                <Tip label={isChecked ? `Deselect ${file.name}` : `Select ${file.name}`} side="top">
                    <button
                        onClick={(e) => { e.stopPropagation(); onToggleSelect(file, e.shiftKey); }}
                        aria-pressed={isChecked}
                        data-testid="file-row-select"
                        className={cn(
                            'flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md border transition-all',
                            isChecked
                                ? 'border-primary bg-primary text-primary-foreground opacity-100'
                                : 'border-white/25 text-transparent hover:border-primary/60',
                            !isChecked && !selectionActive && 'opacity-0 group-hover:opacity-100 max-md:opacity-100',
                        )}
                    >
                        <Check className="h-3 w-3" />
                    </button>
                </Tip>
            )}

            <Tip label={`Open ${file.name}`} side="top">
                <button
                    onClick={() => onPreview(file)}
                    className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-secondary/50 transition-transform group-hover:scale-110"
                >
                    {getFileIcon(file.type, file.name, 'h-6 w-6')}
                </button>
            </Tip>

            <div className="min-w-0 flex-1">
                {isRenaming ? renameField : (
                    <>
                        <div className="flex items-center gap-1.5">
                            <button
                                className={cn(
                                    'min-w-0 truncate text-left font-mono text-sm font-medium transition-colors',
                                    readOnly ? 'cursor-default' : 'hover:text-primary'
                                )}
                                onClick={() => !readOnly && setIsRenaming(true)}
                                title={readOnly ? file.name : 'Click to rename'}
                            >
                                {file.name}
                            </button>
                            {file.pinned && <Pin className="h-3 w-3 flex-shrink-0 fill-accent text-accent" />}
                            {isAdvisor && <AdvisorBadge />}
                        </div>

                        {/* The matched line, when a content search is what put
                            this row on screen. */}
                        {snippet && (
                            <p className="mt-0.5 truncate font-mono text-[10px] leading-relaxed text-muted-foreground">
                                {snippet.before}
                                <mark className="rounded-sm bg-primary/25 px-0.5 text-primary">{snippet.match}</mark>
                                {snippet.after}
                            </p>
                        )}

                        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
                            <p className="whitespace-nowrap text-xs text-muted-foreground">
                                {file.category} · {formatDistanceToNow(new Date(file.last_edited))} ago
                            </p>

                            <div className="flex gap-1">
                                {tags.map((tag) => (
                                    <span
                                        key={tag}
                                        className="group/tag inline-flex items-center gap-0.5 whitespace-nowrap rounded-md border border-primary/30 bg-primary/20 px-1.5 py-0.5 text-[9px] text-primary"
                                    >
                                        <Hash className="h-2 w-2" />
                                        {tag}
                                        {!readOnly && (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); onUpdateTags(file, (file.tags || []).filter((t) => t !== tag)); }}
                                                className="ml-0.5 opacity-0 transition-opacity hover:text-red-400 group-hover/tag:opacity-100"
                                                aria-label={`Remove tag ${tag}`}
                                            >
                                                <X className="h-2 w-2" />
                                            </button>
                                        )}
                                    </span>
                                ))}
                                {isAddingTag ? (
                                    <input
                                        type="text"
                                        value={newTag}
                                        onChange={(e) => setNewTag(e.target.value)}
                                        onBlur={handleAddTag}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') handleAddTag();
                                            if (e.key === 'Escape') { setNewTag(''); setIsAddingTag(false); }
                                        }}
                                        placeholder="tag"
                                        className="w-12 rounded-md border border-primary/30 bg-transparent px-1 py-0.5 text-[9px] outline-none"
                                        autoFocus
                                    />
                                ) : (
                                    !readOnly && (
                                        <button
                                            onClick={() => setIsAddingTag(true)}
                                            className="rounded-md border border-dashed border-white/20 px-1.5 py-0.5 text-[9px] text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary"
                                            aria-label="Add tag"
                                        >
                                            <Plus className="h-2 w-2" />
                                        </button>
                                    )
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>

            <div className="flex flex-shrink-0 items-center gap-1 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100 max-md:opacity-100">
                {/* Both of these used to read "Open", which told you nothing
                    about which one you were about to press. */}
                <Button variant="ghost" size="sm" className="hidden rounded-lg md:inline-flex" onClick={() => onPreview(file)}>
                    Preview
                </Button>
                <Button variant="secondary" size="sm" className="hidden rounded-lg md:inline-flex" onClick={() => onOpen(file)}>
                    Edit
                </Button>
                <RowMenu {...menuProps} triggerClassName="h-8 w-8 rounded-lg" />
            </div>
        </div>
    );
}

/** Every action an artifact has. Shared by both densities so the shelf can never
 *  end up able to do less than the list. */
function RowMenu({
    file, triggerClassName, onOpen, onPreview, onDownload,
    onSendToAdvisor, onPin, onStartRename, onDelete, readOnly,
}) {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="ghost"
                    size="icon"
                    className={cn('flex-shrink-0', triggerClassName)}
                    aria-label={`Actions for ${file.name}`}
                    onClick={(e) => e.stopPropagation()}
                >
                    <MoreVertical className="h-4 w-4" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onOpen(file)}>
                    <ExternalLink className="mr-2 h-4 w-4" /> Open editor
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onPreview(file)}>
                    <Eye className="mr-2 h-4 w-4" /> Open in panel
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onDownload(file)}>
                    <Download className="mr-2 h-4 w-4" /> Download
                </DropdownMenuItem>
                {!readOnly && (
                    <>
                        <DropdownMenuItem onClick={() => onSendToAdvisor(file)}>
                            <MessageSquarePlus className="mr-2 h-4 w-4" /> Send to Advisor
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => onPin(file)}>
                            <Pin className={cn('mr-2 h-4 w-4', file.pinned && 'fill-accent text-accent')} />
                            {file.pinned ? 'Unpin' : 'Pin'}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={onStartRename}>
                            <FileText className="mr-2 h-4 w-4" /> Rename
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="text-red-500" onClick={() => onDelete(file)}>
                            <Trash2 className="mr-2 h-4 w-4" /> Delete
                        </DropdownMenuItem>
                    </>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

/** The grip a shelf column drags by. Kept out of FileRow so the list view,
 *  which has nothing to drag to, never renders a handle it can't honour.
 *
 *  Holds its width at rest and only fades in on hover: the shelf shows a lot of
 *  these at once, and a permanent column of grips reads as noise. */
export function DragHandle(props) {
    return (
        <div
            {...props}
            /* focus-visible matters: dnd-kit makes this tabbable so the keyboard
               sensor has something to lift from, and an invisible tab stop is a
               dead end. */
            className="flex w-3 flex-shrink-0 cursor-grab touch-none justify-center text-muted-foreground/50 opacity-0 outline-none transition-opacity focus-visible:opacity-100 focus-visible:text-primary active:cursor-grabbing group-hover:opacity-100 max-md:opacity-100"
            title="Drag to change category"
        >
            <GripVertical className="h-3.5 w-3.5" />
        </div>
    );
}

export default React.memo(FileRow);
